/**
 * The publications that run on delapresse today. This is the only source
 * for the marketing "showcase" surfaces (home hero collage, trust strip,
 * /vitrine grid). Every entry is a real, live site: the screenshot under
 * /marketing/showcase/<domain>.webp is captured from production.
 *
 * `articles` is a rounded snapshot of published articles (2026-09-02).
 * Refresh it when re-capturing screenshots; do not query the articles
 * table from the marketing site (RLS blocks anonymous reads, and the
 * marketing site must never render customer content).
 */

export type SiteLang = "fr" | "en";

export interface ShowcaseSite {
  domain: string;
  name: string;
  lang: SiteLang;
  niche: { fr: string; en: string; es?: string; ar?: string };
  articles: number;
  /** Order in the hero collage and the home grid. Lower first. */
  featured?: number;
}

export const SHOWCASE_SITES: ShowcaseSite[] = [
  {
    domain: "thehackingpost.com",
    name: "The Hacking Post",
    lang: "en",
    niche: { fr: "Cybersécurité", en: "Cybersecurity", es: "Ciberseguridad", ar: "الأمن السيبراني" },
    articles: 23100,
    featured: 1,
  },
  {
    domain: "presseunie.fr",
    name: "Presse Unie",
    lang: "fr",
    niche: { fr: "Actualité générale", en: "General news", es: "Noticias generales", ar: "أخبار عامة" },
    articles: 8100,
    featured: 2,
  },
  {
    domain: "thelevantherald.com",
    name: "The Levant Herald",
    lang: "en",
    niche: { fr: "Levant et Proche-Orient", en: "The Levant", es: "El Levante", ar: "الشرق الأوسط" },
    articles: 2000,
    featured: 3,
  },
  {
    domain: "theafricanherald.com",
    name: "The African Herald",
    lang: "en",
    niche: { fr: "Afrique", en: "Africa", es: "África", ar: "أفريقيا" },
    articles: 2700,
    featured: 4,
  },
  {
    domain: "metroboulotdodo.fr",
    name: "Métro Boulot Dodo",
    lang: "fr",
    niche: { fr: "Travail, ville et vie quotidienne", en: "Work, city and daily life", es: "Trabajo, ciudad y vida cotidiana", ar: "العمل والحياة اليومية والمدن" },
    articles: 2300,
    featured: 5,
  },
  {
    domain: "cybercanal.fr",
    name: "Cybercanal",
    lang: "fr",
    niche: { fr: "Cybersécurité", en: "Cybersecurity", es: "Ciberseguridad", ar: "الأمن السيبراني" },
    articles: 2200,
    featured: 6,
  },
  {
    domain: "thegulftribune.com",
    name: "The Gulf Tribune",
    lang: "en",
    niche: { fr: "Économie et marchés du Golfe", en: "Gulf business and markets", es: "Negocios y mercados del Golfo", ar: "الأعمال والأسواق الخليجية" },
    articles: 2000,
  },
  {
    domain: "thesaudiherald.com",
    name: "The Saudi Herald",
    lang: "en",
    niche: { fr: "Arabie saoudite", en: "Saudi Arabia", es: "Arabia Saudí", ar: "المملكة العربية السعودية" },
    articles: 1400,
  },
  {
    domain: "echostricolores.fr",
    name: "Échos Tricolores",
    lang: "fr",
    niche: { fr: "Politique et société françaises", en: "French politics and society", es: "Política y sociedad francesas", ar: "السياسة والمجتمع الفرنسي" },
    articles: 1100,
  },
  {
    domain: "libanaisaparis.fr",
    name: "Libanais à Paris",
    lang: "fr",
    niche: { fr: "Communauté libanaise en France", en: "Lebanese community in France", es: "Comunidad libanesa en Francia", ar: "المجتمع اللبناني في فرنسا" },
    articles: 70,
  },
];

export const FEATURED_SITES = [...SHOWCASE_SITES]
  .filter((s) => s.featured)
  .sort((a, b) => (a.featured ?? 99) - (b.featured ?? 99));

export function screenshotUrl(site: ShowcaseSite, size: "lg" | "sm" = "lg"): string {
  return `/marketing/showcase/${site.domain}${size === "sm" ? "-sm" : ""}.webp`;
}

export function siteUrl(site: ShowcaseSite): string {
  return `https://${site.domain}/`;
}
